import { PhoneCall } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";

interface ScheduleVoiceCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardStats: {
    pendingATS: number;
    selected: number;
    todayApplications: number;
  };
  scheduleData: {
    candidates: string;
    callTime: string;
    questions: string;
  };
  setScheduleData: Dispatch<SetStateAction<{
      candidates: string;
      callTime: string;
      questions: string;
    }>>;
}

export default function ScheduleVoiceCallDialog({
  open,
  onOpenChange,
  dashboardStats,
  scheduleData,
  setScheduleData,
}: ScheduleVoiceCallDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneCall className="h-5 w-5" />
            Schedule Voice Call Campaign
          </DialogTitle>
          <DialogDescription>
            Configure AI voice agent to call candidates
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Target Candidates</label>
            <Select
              value={scheduleData.candidates}
              onValueChange={(value) =>
                setScheduleData((current) => ({
                  ...current,
                  candidates: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select candidates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-pending-ats">
                  All Pending ATS ({dashboardStats.pendingATS})
                </SelectItem>
                <SelectItem value="selected">
                  Selected Candidates ({dashboardStats.selected})
                </SelectItem>
                <SelectItem value="today">
                  Today's Applications ({dashboardStats.todayApplications})
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Question Set</label>
            <Select
              value={scheduleData.questions}
              onValueChange={(value) =>
                setScheduleData((current) => ({
                  ...current,
                  questions: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard (10 questions)</SelectItem>
                <SelectItem value="quick">Quick (5 questions)</SelectItem>
                <SelectItem value="detailed">Detailed (15 questions)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Call Window</label>
            <Input
              type="datetime-local"
              value={scheduleData.callTime}
              onChange={(event) =>
                setScheduleData((current) => ({
                  ...current,
                  callTime: event.target.value,
                }))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>

          <Button>
            <PhoneCall className="mr-2 h-4 w-4" />
            Schedule Calls
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
